/**
 * Tracking provider registry
 * Manages registration and dispatch to all tracking providers
 */

import type { TrackingProvider, UserProperties, GroupProperties } from './types'

const providers: TrackingProvider[] = []

export function registerProvider(provider: TrackingProvider): void {
  if (providers.some(p => p.name === provider.name)) {
    console.warn(`Provider "${provider.name}" is already registered`)
    return
  }
  providers.push(provider)
}

export function getProviders(): readonly TrackingProvider[] {
  return providers
}

export function clearProviders(): void {
  providers.length = 0
}

async function dispatchToAll<T>(
  operation: string,
  fn: (provider: TrackingProvider) => Promise<T>
): Promise<void> {
  await Promise.all(
    providers.map(async provider => {
      try {
        await fn(provider)
      } catch (error) {
        console.error(`[track:${provider.name}] ${operation} failed:`, error)
      }
    })
  )
}

export async function trackAll(
  eventName: string,
  properties: Record<string, unknown>
): Promise<void> {
  await dispatchToAll('track', p => p.track(eventName, properties))
}

export async function identifyAll(
  userId: string,
  properties?: UserProperties
): Promise<void> {
  await dispatchToAll('identify', p => p.identify(userId, properties))
}

export async function resetAll(): Promise<void> {
  await dispatchToAll('reset', p => p.reset())
}

export async function setGroupAll(
  groupKey: string,
  groupId: string | string[]
): Promise<void> {
  await dispatchToAll('setGroup', p => p.setGroup(groupKey, groupId))
}

export async function identifyGroupAll(
  groupKey: string,
  groupId: string,
  properties?: GroupProperties
): Promise<void> {
  await dispatchToAll('identifyGroup', p =>
    p.identifyGroup(groupKey, groupId, properties)
  )
}

export async function clearGroupAll(groupKey: string): Promise<void> {
  await dispatchToAll('clearGroup', p => p.clearGroup(groupKey))
}
